<template>
  <div class="flexible-chart-builder min-h-screen bg-gray-50">
    <!-- Header mobile-optimized -->
    <div class="bg-white border-b border-gray-200 sticky top-0 z-10">
      <div class="flex items-center justify-between p-4">
        <div>
          <h2 class="text-lg font-semibold text-gray-900">
            {{ isEditing ? $t('customcharts.create.title_edit') : $t('customcharts.create.title_new') }}
          </h2>
          <p class="text-sm text-gray-600">
            {{ t('customcharts.builder.subtitle') }}
          </p>
        </div>
        <button @click="$emit('cancel')"
          class="p-2 text-gray-500 hover:text-gray-700 rounded-full touch-manipulation">
          <svg class="w-6 h-6" fill="none" stroke="currentColor"
            viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round"
              stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Progress indicator -->
      <div class="px-4 pb-2">
        <div class="flex justify-between text-xs text-gray-500 mb-1">
          <span>{{ t('customcharts.builder.step_progress', { current: currentStep, total: totalSteps }) }}</span>
          <span>{{ Math.round((currentStep / totalSteps) * 100) }}%</span>
        </div>
        <div class="w-full bg-gray-200 rounded-full h-1">
          <div class="bg-blue-600 h-1 rounded-full transition-all duration-300"
            :style="{ width: `${(currentStep / totalSteps) * 100}%` }"></div>
        </div>
      </div>
    </div>

    <!-- Validation Status (sticky) -->
    <div v-if="showValidation"
      class="bg-white border-b border-gray-200 sticky top-16 z-9 p-4">
      <ValidationStatus :validation-result="validationResult"
        :config="chartConfig" :is-mobile="isMobile"
        @config-update="updateConfig" @quick-action="handleQuickAction" />
    </div>

    <!-- Main content avec navigation par swipe -->
    <div class="flex-1 overflow-hidden">
      <!-- Navigation dots (mobile) -->
      <div v-if="isMobile" class="flex justify-center py-4 space-x-2">
        <button v-for="(step, index) in steps" :key="step.id"
          @click="goToStep(index + 1)" :class="[
            'w-2 h-2 rounded-full transition-all touch-manipulation',
            currentStep === index + 1
              ? 'bg-blue-600 w-6'
              : 'bg-gray-300 hover:bg-gray-400'
          ]">
        </button>
      </div>

      <!-- Steps content -->
      <div class="relative h-full">
        <Transition :name="transitionName" mode="out-in">
          <div :key="currentStep" class="h-full overflow-y-auto">
            <div class="p-4 space-y-6 pb-24">
              <!-- Extra padding pour boutons flottants -->
              <!-- Étape 1: Nom du graphique -->
              <div v-if="currentStep === 1" class="step-content">
                <div class="bg-white rounded-lg shadow-sm p-4">
                  <h3 class="text-base font-medium text-gray-900 mb-3">
                    {{ t('customcharts.builder.chart_name') }}
                  </h3>
                  <input v-model="chartConfig.name" type="text" required
                    :placeholder="$t('customcharts.placeholders.chart_name')"
                    class="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base">
                </div>
              </div>

              <!-- Étape 2: Type de graphique -->
              <div v-if="currentStep === 2" class="step-content">
                <div class="bg-white rounded-lg shadow-sm p-4">
                  <ChartTypeSelector 
                    v-model="coordinateSystemValue"
                    :default-chart-type="getDefaultChartType()"
                    @update:default-chart-type="updateDefaultChartType"
                    :compact-mode="isMobile" 
                    @change="handleCoordinateSystemChange" />
                </div>
              </div>

              <!-- Étape 3: Sources de données -->
              <div v-if="currentStep === 3" class="step-content">
                <div class="bg-white rounded-lg shadow-sm p-4">
                  <h3 class="text-base font-medium text-gray-900 mb-3">
                    {{ t('customcharts.builder.data_sources') }}
                  </h3>

                  <!-- Liste des sources existantes -->
                  <div class="space-y-6 mb-6">
                    <div v-for="(dataSource, index) in chartConfig.dataSources"
                      :key="dataSource.id" class="space-y-3">

                      <!-- En-tête série avec séparateur -->
                      <div class="flex items-center gap-3">
                        <div class="flex items-center gap-2 flex-1">
                          <div class="text-sm font-medium text-gray-500">{{ t('customcharts.builder.series') }}
                            {{ index + 1 }}</div>
                          <div class="flex-1 h-px bg-gray-200"></div>
                        </div>
                        <button v-if="chartConfig.dataSources.length > 1"
                          @click="removeDataSource(index)"
                          class="text-red-500 hover:text-red-700 p-1 touch-manipulation">
                          <svg class="w-4 h-4" fill="currentColor"
                            viewBox="0 0 20 20">
                            <path fill-rule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clip-rule="evenodd" />
                          </svg>
                        </button>
                      </div>

                      <!-- Nom de la série -->
                      <div>
                        <label class="block text-xs text-gray-600 mb-1">{{ t('customcharts.builder.series_name') }}</label>
                        <input v-model="dataSource.name"
                          :placeholder="$t('customcharts.placeholders.series_name')"
                          class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                      </div>

                      <!-- Configuration de la source -->
                      <div class="space-y-3">
                        <!-- Source -->
                        <div>
                          <label class="block text-xs text-gray-600 mb-1">{{ t('customcharts.builder.data_source') }}</label>
                          <Select v-model="dataSource.source"
                            @update:model-value="onSourceChange(dataSource)">
                            <SelectTrigger class="w-full">
                              <SelectValue
                                :placeholder="$t('customcharts.placeholders.select_source')" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="sales">📊 {{ t('customcharts.builder.sales_data') }}</SelectItem>
                              <SelectItem value="products">🛍️ {{ t('customcharts.builder.products_data') }}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <!-- Champ à analyser -->
                        <div>
                          <label class="block text-xs text-gray-600 mb-1">{{ t('customcharts.builder.field_to_analyze') }}</label>
                          <Select v-model="dataSource.field"
                            @update:model-value="onFieldChange(dataSource)">
                            <SelectTrigger class="w-full">
                              <SelectValue :placeholder="$t('customcharts.placeholders.choose_field')">
                                <span v-if="dataSource.field">
                                  {{ getSelectedFieldLabel(dataSource.source,
                                  dataSource.field) }}
                                </span>
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent class="max-h-80">
                              <div
                                v-for="category in getFieldCategories(dataSource.source)"
                                :key="category">
                                <SelectGroup>
                                  <SelectLabel
                                    class="px-2 py-1.5 text-xs font-medium text-gray-500">
                                    {{ getCategoryLabel(category) }}
                                  </SelectLabel>
                                  <SelectItem
                                    v-for="field in getFieldsByCategory(dataSource.source, category)"
                                    :key="field.key" :value="field.key"
                                    class="pl-6">
                                    <div
                                      class="flex items-center justify-between w-full">
                                      <div class="flex-1 min-w-0">
                                        <div class="font-medium text-sm">{{
                                          field.label }}</div>
                                        <div
                                          class="text-xs text-gray-500 truncate">
                                          {{ field.description }}</div>
                                      </div>
                                      <div
                                        class="text-xs px-1.5 py-0.5 bg-gray-100 rounded text-gray-600 font-mono ml-2">
                                        {{ getFieldTypeLabel(field.type) }}
                                      </div>
                                    </div>
                                  </SelectItem>
                                </SelectGroup>
                              </div>
                            </SelectContent>
                          </Select>
                        </div>

                        <!-- Origine des données (web/all) -->
                        <div v-if="dataSource.source === 'sales'">
                          <label class="block text-xs text-gray-600 mb-1">{{ t('customcharts.builder.data_origin') }}</label>
                          <Select v-model="dataSource.origin">
                            <SelectTrigger class="w-full">
                              <SelectValue :placeholder="$t('customcharts.placeholders.all_sales')" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">🏪 {{ t('customcharts.builder.all_sales') }}</SelectItem>
                              <SelectItem value="web">🌐 {{ t('customcharts.builder.web_sales_only') }}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <!-- Type de données (ventes/acomptes) -->
                        <div v-if="dataSource.source === 'sales'">
                          <label class="block text-xs text-gray-600 mb-1">{{ t('customcharts.builder.data_type') }}</label>
                          <Select
                            :model-value="dataSource.deposit ? 'true' : 'false'"
                            @update:model-value="(value) => dataSource.deposit = value === 'true'">
                            <SelectTrigger class="w-full">
                              <SelectValue :placeholder="$t('customcharts.placeholders.sales_data')" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="false">💰 {{ t('customcharts.builder.sales_revenue') }}</SelectItem>
                              <SelectItem value="true">💳 {{ t('customcharts.builder.deposits_data') }}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <!-- Sélecteur d'agrégation -->
                      <div class="mt-3">
                        <AggregationSelector v-model="dataSource.aggregation"
                          :field-name="dataSource.field"
                          :field-type="getFieldType(dataSource.field)"
                          :compact-mode="true" :show-advanced="true" />
                      </div>

                      <!-- Sélecteur de type de graphique par série -->
                      <div v-if="coordinateSystemValue && COORDINATE_SYSTEMS[coordinateSystemValue].supportsMultipleSeries" 
                           class="mt-3">
                        <label class="block text-xs text-gray-600 mb-2">
                          {{ t('customcharts.builder.visualization_type') }}
                        </label>
                        <div class="flex gap-2 overflow-x-auto pb-1">
                          <button 
                            v-for="chartType in getAvailableChartTypes(coordinateSystemValue)" 
                            :key="chartType"
                            @click="updateSeriesChartType(dataSource.id, chartType)"
                            :class="[
                              'flex-shrink-0 px-3 py-2 rounded-lg border transition-all touch-manipulation text-xs',
                              'flex items-center gap-2 min-w-0',
                              (dataSource.chartType || getDefaultChartType()) === chartType
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : 'border-gray-200 hover:border-gray-300 text-gray-700'
                            ]">
                            <span>{{ getChartTypeIcon(chartType) }}</span>
                            <span class="truncate">{{ getChartTypeLabel(chartType) }}</span>
                          </button>
                        </div>
                        
                        <!-- Info sur la compatibilité -->
                        <div class="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-600">
                          <span class="font-medium">💡 {{ t('customcharts.builder.tip_label') }}</span> {{ t('customcharts.builder.tip_mix_types') }}
                        </div>
                      </div>

                      <!-- Message pour graphiques polaires (pas de mélange possible) -->
                      <div v-else-if="coordinateSystemValue === 'polar'" 
                           class="mt-3 p-2 bg-amber-50 rounded text-xs text-amber-600">
                        <span class="font-medium">ℹ️ {{ t('customcharts.builder.info_label') }}</span> {{ COORDINATE_SYSTEMS.polar.name.toLowerCase() }} {{ t('customcharts.builder.info_single_series') }}
                      </div>
                    </div>
                  </div>

                  <!-- Bouton ajouter source -->
                  <button v-if="canAddDataSource" @click="addDataSource"
                    class="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors touch-manipulation">
                    <svg class="w-5 h-5 inline-block mr-2" fill="currentColor"
                      viewBox="0 0 20 20">
                      <path fill-rule="evenodd"
                        d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                        clip-rule="evenodd" />
                    </svg>
                    {{ t('customcharts.builder.add_data_series') }}
                  </button>
                </div>
              </div>

              <!-- Étape 4: Configuration période (existante adaptée) -->
              <div v-if="currentStep === 4" class="step-content">
                <div class="bg-white rounded-lg shadow-sm p-4">
                  <h3 class="text-base font-medium text-gray-900 mb-3">
                    {{ t('customcharts.builder.analysis_period') }}
                  </h3>

                  <!-- Sélection période principale -->
                  <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-3">
                      {{ t('customcharts.builder.period_required') }}
                    </label>
                    <Popover>
                      <PopoverTrigger as-child>
                        <button type="button"
                          class="w-full px-3 py-3 border border-gray-300 rounded-lg text-left hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex items-center justify-between">
                          <span
                            v-if="selectedRange && selectedRange.start && selectedRange.end"
                            class="text-gray-900">
                            {{ formatDateRange(selectedRange) }}
                          </span>
                          <span v-else class="text-gray-400">
                            {{ t('customcharts.builder.select_period') }}
                          </span>
                          <span class="text-gray-400 text-sm">📅</span>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent class="w-auto p-0" align="start">
                        <RangeCalendar v-model="selectedRange"
                          @update:model-value="updatePeriod"
                          :number-of-months="1" :max-value="maxDate"
                          class="bg-white" />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <!-- Sélection agrégation temporelle -->
                  <div v-if="chartConfig.xAxis.type === 'time'" class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-3">
                      {{ t('customcharts.builder.time_aggregation') }}
                    </label>
                    <div class="space-y-3">
                      <!-- Agrégations recommandées -->
                      <div>
                        <div class="text-xs text-gray-500 mb-2">{{ t('customcharts.builder.recommended') }}
                        </div>
                        <div class="grid grid-cols-2 gap-2">
                          <button
                            v-for="timeAgg in availableTimeAggregations.filter(t => t.smartDetection)"
                            :key="timeAgg.type"
                            @click="chartConfig.xAxis.timeAggregation = timeAgg.type"
                            :class="[
                              'p-3 border rounded-lg text-left transition-all touch-manipulation',
                              chartConfig.xAxis.timeAggregation === timeAgg.type
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : 'border-gray-200 hover:border-gray-300 text-gray-700'
                            ]">
                            <div class="font-medium text-sm">{{ timeAgg.label }}
                            </div>
                            <div class="text-xs text-gray-500 mt-1">{{
                              timeAgg.description }}</div>
                          </button>
                        </div>
                      </div>

                      <!-- Agrégations avancées -->
                      <div>
                        <div class="text-xs text-gray-500 mb-2">{{ t('customcharts.builder.advanced_analyses') }}</div>
                        <div class="grid grid-cols-1 gap-2">
                          <button
                            v-for="timeAgg in availableTimeAggregations.filter(t => !t.smartDetection)"
                            :key="timeAgg.type"
                            @click="chartConfig.xAxis.timeAggregation = timeAgg.type"
                            :class="[
                              'p-3 border rounded-lg text-left transition-all touch-manipulation relative',
                              chartConfig.xAxis.timeAggregation === timeAgg.type
                                ? 'border-purple-500 bg-purple-50 text-purple-700'
                                : 'border-gray-200 hover:border-gray-300 text-gray-700'
                            ]">
                            <div class="flex items-center gap-2">
                              <div class="font-medium text-sm">{{ timeAgg.label
                                }}</div>
                              <span
                                class="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">{{ t('customcharts.builder.advanced') }}</span>
                            </div>
                            <div class="text-xs text-gray-500 mt-1">{{
                              timeAgg.description }}</div>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Toggle comparaison -->
                  <div class="mb-4">
                    <label
                      class="flex items-center gap-2 text-sm font-medium text-gray-700">
                      <input type="checkbox"
                        v-model="chartConfig.showComparison"
                        class="rounded border-gray-300 text-blue-600 focus:ring-blue-500">
                      <span>{{ t('customcharts.builder.compare_period') }}</span>
                    </label>

                    <!-- Période de comparaison -->
                    <Transition name="slide-down">
                      <div v-if="chartConfig.showComparison"
                        class="comparison-section mt-3">
                        <div class="text-xs text-gray-600 mb-2">{{ t('customcharts.builder.comparison_period') }}</div>
                        <Popover>
                          <PopoverTrigger as-child>
                            <button type="button"
                              class="w-full px-3 py-3 border border-gray-300 rounded-lg text-left hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex items-center justify-between bg-white transition-all">
                              <span
                                v-if="comparisonRange && comparisonRange.start && comparisonRange.end"
                                class="text-gray-900">
                                {{ formatDateRange(comparisonRange) }}
                              </span>
                              <span v-else class="text-gray-400">
                                {{ t('customcharts.builder.select_comparison_period') }}
                              </span>
                              <span class="text-gray-400 text-sm">📅</span>
                            </button>
                          </PopoverTrigger>
                          <PopoverContent class="w-auto p-0" align="start">
                            <RangeCalendar v-model="comparisonRange"
                              @update:model-value="updateComparisonPeriod"
                              :number-of-months="1" :max-value="maxDate"
                              class="bg-white" />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </Transition>
                  </div>
                </div>
              </div>

              <!-- Étape 5: Prévisualisation -->
              <div v-if="currentStep === 5" class="step-content">
                <div class="bg-white rounded-lg shadow-sm p-4">
                  <h3 class="text-base font-medium text-gray-900 mb-4">
                    {{ t('customcharts.builder.chart_preview') }}
                  </h3>

                  <!-- Rendu du graphique -->
                  <ChartRenderer :config="chartConfig" :is-mobile="isMobile"
                    :compact-mode="false" :show-header="false"
                    :show-stats="true" :show-debug-info="false"
                    :center-id="centerId" :height="isMobile ? 280 : 350"
                    @data-point-click="handleDataPointClick"
                    @chart-ready="handleChartReady" @error="handleChartError" />

                  <!-- Résumé de la configuration -->
                  <div class="mt-4 p-3 bg-gray-50 rounded-lg">
                    <h4 class="text-sm font-medium text-gray-900 mb-2">
                      {{ t('customcharts.builder.config_summary') }}
                    </h4>
                    <div class="grid grid-cols-1 gap-2 text-xs text-gray-600">
                      <div class="flex justify-between">
                        <span>{{ t('customcharts.builder.chart_type_label') }}</span>
                        <span class="font-medium">{{
                          getChartTypeLabel(chartConfig.chartType) }}</span>
                      </div>
                      <div class="flex justify-between">
                        <span>{{ t('customcharts.builder.series_count') }}</span>
                        <span class="font-medium">{{
                          chartConfig.dataSources.length }}</span>
                      </div>
                      <div class="flex justify-between">
                        <span>{{ t('customcharts.builder.period_label') }}</span>
                        <span class="font-medium">{{ formatConfigPeriod()
                          }}</span>
                      </div>
                      <div v-if="chartConfig.xAxis.timeAggregation"
                        class="flex justify-between">
                        <span>{{ t('customcharts.builder.time_aggregation_label') }}</span>
                        <span class="font-medium">{{
                          getTimeAggregationLabel(chartConfig.xAxis.timeAggregation)
                          }}</span>
                      </div>
                    </div>

                    <!-- Détail des séries -->
                    <div class="mt-3 pt-3 border-t border-gray-200">
                      <div class="text-xs text-gray-500 mb-2">{{ t('customcharts.builder.data_series_label') }}</div>
                      <div class="space-y-1">
                        <div v-for="(ds) in chartConfig.dataSources"
                          :key="ds.id" class="flex items-center gap-2 text-xs">
                          <div class="w-2 h-2 rounded-full bg-blue-500"></div>
                          <span class="font-medium">{{ ds.name }}</span>
                          <span class="text-gray-500">{{
                            getAggregationLabel(ds.aggregation) }} de {{
                            ds.field }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </Transition>
      </div>
    </div>

    <!-- Navigation buttons (mobile floating) -->
    <div
      class="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
      <div class="flex gap-3">
        <button v-if="currentStep > 1" @click="previousStep"
          class="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium touch-manipulation">
          {{ t('Previous') }}
        </button>

        <button v-if="currentStep < totalSteps" @click="nextStep"
          :disabled="!canProceedToNextStep"
          class="flex-1 py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium touch-manipulation">
          {{ t('Next') }}
        </button>

        <button v-if="currentStep === totalSteps" @click="handleSave"
          :disabled="!isFormValid"
          class="flex-1 py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium touch-manipulation">
          {{ isEditing ? $t('customcharts.create.button_edit') : $t('customcharts.create.button_create') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  createDefaultFlexibleChartConfig,
  generateDataSourceId,
  FLEXIBLE_CHART_TYPE_OPTIONS,
  COORDINATE_SYSTEMS,
  getAvailableChartTypes,
  TIME_AGGREGATIONS,
  AGGREGATION_TYPES,
  getFieldsForSource,
  getFieldMetadata,
  type CustomChart,
  type FlexibleChartConfig,
  type DataSourceConfig,
  type DataSource,
  type FieldMetadata,
  type CoordinateSystem,
  type FlexibleChartType
} from '../../types/customCharts'
import { chartValidator, type ValidationResult } from '../../utils/chartValidation'
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover'
import { RangeCalendar } from '../../components/ui/range-calendar'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '../../components/ui/select'
import type { DateRange } from 'reka-ui'
import { toDate } from 'reka-ui/date'
import { CalendarDate } from '@internationalized/date'

// Composants
import ChartTypeSelector from '../../components/charts/ChartTypeSelector.vue'
import AggregationSelector from '../../components/charts/AggregationSelector.vue'
import ValidationStatus from '../../components/charts/ValidationStatus.vue'
import ChartRenderer from '../../components/charts/ChartRenderer.vue'

interface Props {
  chart?: CustomChart | null
  centerId: number
  reportId: string
  isMobile?: boolean
}

interface Emits {
  (e: 'save', chart: CustomChart): void
  (e: 'cancel'): void
}

const props = withDefaults(defineProps<Props>(), {
  isMobile: true
})

const emit = defineEmits<Emits>()

// État global
const currentStep = ref(1)
const transitionName = ref('slide-right')
const selectedRange = ref<DateRange | undefined>()
const comparisonRange = ref<DateRange | undefined>()
const validationResult = ref<ValidationResult>({ valid: true, errors: [], warnings: [], infos: [] })

// Configuration du graphique (réactive)
const chartConfig = reactive<FlexibleChartConfig & { name: string }>({
  name: '',
  ...createDefaultFlexibleChartConfig()
})

// Configuration des étapes
const { t } = useI18n()
const steps = computed(() => [
  { id: 'name', title: t('customcharts.tabs.name') },
  { id: 'type', title: t('customcharts.tabs.type') },
  { id: 'data', title: t('customcharts.tabs.data') },
  { id: 'period', title: t('customcharts.tabs.period') },
  { id: 'preview', title: t('customcharts.tabs.preview') }
])

const totalSteps = computed(() => steps.value.length)
const isEditing = computed(() => !!props.chart)

// Dates
const today = new Date()
const maxDate = new CalendarDate(today.getFullYear(), today.getMonth() + 1, today.getDate())

// Validation
const showValidation = computed(() => currentStep.value > 1)

// Computed property pour coordinateSystem avec fallback
const coordinateSystemValue = computed({
  get: () => chartConfig.coordinateSystem || 'cartesian',
  set: (value: CoordinateSystem) => {
    chartConfig.coordinateSystem = value
  }
})

const isFormValid = computed(() => {
  return validationResult.value.valid &&
    chartConfig.name.trim() !== '' &&
    chartConfig.period.start !== '' &&
    chartConfig.period.end !== ''
})

const canProceedToNextStep = computed(() => {
  switch (currentStep.value) {
    case 1:
      return chartConfig.name.trim() !== ''
    case 2:
      return true // Chart type is always valid since it has a default value
    case 3:
      return chartConfig.dataSources.length > 0 &&
        chartConfig.dataSources.every(ds => ds.name.trim() !== '')
    case 4:
      return chartConfig.period.start !== '' && chartConfig.period.end !== ''
    case 5:
      return validationResult.value.valid // Prévisualisation nécessite une config valide
    default:
      return true
  }
})

// Sources de données
const canAddDataSource = computed(() => {
  const selectedType = FLEXIBLE_CHART_TYPE_OPTIONS.find(opt => opt.type === chartConfig.chartType)
  return selectedType && chartConfig.dataSources.length < selectedType.maxDataSources
})

// Agrégations temporelles disponibles
const availableTimeAggregations = computed(() => {
  // Séparer les agrégations par catégorie
  const smart = TIME_AGGREGATIONS.filter(timeAgg => timeAgg.smartDetection)
  const advanced = TIME_AGGREGATIONS.filter(timeAgg => !timeAgg.smartDetection)

  // Retourner toutes les agrégations (recommandées en premier)
  return [...smart, ...advanced]
})

// Navigation
const nextStep = () => {
  if (currentStep.value < totalSteps.value && canProceedToNextStep.value) {
    transitionName.value = 'slide-left'
    currentStep.value++
  }
}

const previousStep = () => {
  if (currentStep.value > 1) {
    transitionName.value = 'slide-right'
    currentStep.value--
  }
}

const goToStep = (step: number) => {
  if (step >= 1 && step <= totalSteps.value) {
    transitionName.value = step > currentStep.value ? 'slide-left' : 'slide-right'
    currentStep.value = step
  }
}

// Gestion des données
const addDataSource = () => {
  if (!canAddDataSource.value) return

  // Utiliser le premier champ disponible de la source sales par défaut
  const salesFields = getFieldsForSource('sales')
  const defaultField = salesFields[0]

  const newDataSource: DataSourceConfig = {
    id: generateDataSourceId(),
    name: `${t('customcharts.builder.series')} ${chartConfig.dataSources.length + 1}`,
    source: 'sales',
    aggregation: defaultField.suggestedAggregations[0],
    field: defaultField.key,
    limit: 100,
    origin: 'all',
    deposit: false
  }

  chartConfig.dataSources.push(newDataSource)
}

const removeDataSource = (index: number) => {
  if (chartConfig.dataSources.length > 1) {
    chartConfig.dataSources.splice(index, 1)
  }
}

// Méthodes pour la gestion des champs par source
// const getFieldsForDataSource = (source: DataSource): FieldMetadata[] => {
//   return getFieldsForSource(source)
// }

const getFieldCategories = (source: DataSource): string[] => {
  const fields = getFieldsForSource(source)
  const categories = [...new Set(fields.map(f => f.category))]

  // Ordre préféré des catégories
  const order = ['revenue', 'volume', 'performance', 'metric', 'identification']
  return categories.sort((a, b) => {
    const aIndex = order.indexOf(a)
    const bIndex = order.indexOf(b)
    return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex)
  })
}

const getFieldsByCategory = (source: DataSource, category: string): FieldMetadata[] => {
  return getFieldsForSource(source).filter(f => f.category === category)
}

const getSelectedFieldLabel = (source: DataSource, field: string): string => {
  return getFieldMetadata(source, field)?.label || field
}

const getCategoryLabel = (category: string): string => {
  const labels: Record<string, string> = {
    'revenue': '💰 ' + t('customcharts.builder.categories.revenue'),
    'volume': '📊 ' + t('customcharts.builder.categories.volume'),
    'performance': '🎯 ' + t('customcharts.builder.categories.performance'),
    'metric': '📈 ' + t('customcharts.builder.categories.metric'),
    'identification': '🏷️ ' + t('customcharts.builder.categories.identification')
  }
  return labels[category] || category
}

const getFieldTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    'currency': '€',
    'number': '#',
    'integer': '0',
    'percentage': '%',
    'string': 'ABC',
    'boolean': '✓'
  }
  return labels[type] || type
}

const getFieldType = (field: string): string => {
  // Chercher dans toutes les sources
  for (const source of ['sales', 'products'] as DataSource[]) {
    const fieldMeta = getFieldMetadata(source, field)
    if (fieldMeta) return fieldMeta.type
  }
  return 'number'
}

// Gestionnaires d'événements
const onSourceChange = (dataSource: DataSourceConfig) => {
  // Réinitialiser le champ quand la source change
  const fields = getFieldsForSource(dataSource.source)
  if (fields.length > 0) {
    dataSource.field = fields[0].key
    dataSource.aggregation = fields[0].suggestedAggregations[0]
  }

  // Initialiser les paramètres API selon la source
  if (dataSource.source === 'sales') {
    dataSource.origin = dataSource.origin || 'all'
    dataSource.deposit = dataSource.deposit || false
  }
}

const onFieldChange = (dataSource: DataSourceConfig) => {
  // Mettre à jour l'agrégation suggérée quand le champ change
  const fieldMeta = getFieldMetadata(dataSource.source, dataSource.field)
  if (fieldMeta && fieldMeta.suggestedAggregations.length > 0) {
    dataSource.aggregation = fieldMeta.suggestedAggregations[0]
  }
}

// Gestion des événements
// const handleChartTypeChange = (option: ChartTypeOption) => {
//   // Ajuster les sources de données selon les contraintes du type
//   if (option.maxDataSources < chartConfig.dataSources.length) {
//     chartConfig.dataSources = chartConfig.dataSources.slice(0, option.maxDataSources)
//   }

//   // Supprimer l'axe secondaire si non supporté
//   if (!option.supportsSecondaryAxis) {
//     chartConfig.yAxis2 = undefined
//   }
// }

// Nouvelles méthodes pour le système de coordonnées
const handleCoordinateSystemChange = (data: { coordinateSystem: CoordinateSystem, defaultChartType: FlexibleChartType }) => {
  chartConfig.coordinateSystem = data.coordinateSystem
  
  // Ajuster les contraintes selon le système
  const systemConfig = COORDINATE_SYSTEMS[data.coordinateSystem]
  
  // Pour les systèmes polaires, limiter à une seule série
  if (!systemConfig.supportsMultipleSeries && chartConfig.dataSources.length > 1) {
    chartConfig.dataSources = chartConfig.dataSources.slice(0, 1)
  }
  
  // Supprimer l'axe secondaire si non supporté
  if (!systemConfig.supportsSecondaryAxis) {
    chartConfig.yAxis2 = undefined
  }
  
  // Assigner le type par défaut aux séries qui n'en ont pas
  chartConfig.dataSources.forEach(ds => {
    if (!ds.chartType) {
      ds.chartType = data.defaultChartType
    }
  })
}

const getDefaultChartType = (): FlexibleChartType => {
  // Retourner le premier type disponible du système de coordonnées sélectionné
  const availableTypes = getAvailableChartTypes(coordinateSystemValue.value)
  return availableTypes[0] || 'line'
}

const updateDefaultChartType = (chartType: FlexibleChartType) => {
  // Optionnel : appliquer le nouveau type par défaut aux séries qui utilisent l'ancien défaut
  const oldDefault = getDefaultChartType()
  chartConfig.dataSources.forEach(ds => {
    if (!ds.chartType || ds.chartType === oldDefault) {
      ds.chartType = chartType
    }
  })
}

const updateSeriesChartType = (seriesId: string, chartType: FlexibleChartType) => {
  const dataSource = chartConfig.dataSources.find(ds => ds.id === seriesId)
  if (dataSource) {
    dataSource.chartType = chartType
  }
}

const getChartTypeIcon = (chartType: FlexibleChartType): string => {
  const option = FLEXIBLE_CHART_TYPE_OPTIONS.find(opt => opt.type === chartType)
  return option?.icon || '📊'
}

const getChartTypeLabel = (chartType: FlexibleChartType): string => {
  const option = FLEXIBLE_CHART_TYPE_OPTIONS.find(opt => opt.type === chartType)
  return option?.label || chartType
}

const updateConfig = (updates: Partial<FlexibleChartConfig>) => {
  Object.assign(chartConfig, updates)
}

const handleQuickAction = (actionId: string) => {
  console.log('Quick action executed:', actionId)
}

// Gestion des dates (repris de l'ancien ChartBuilder)
const formatDateRange = (range: DateRange): string => {
  if (!range?.start || !range?.end) return ''
  const start = toDate(range.start).toLocaleDateString('fr-FR')
  const end = toDate(range.end).toLocaleDateString('fr-FR')
  return `${start} - ${end}`
}

const updatePeriod = (range: DateRange | undefined) => {
  if (range?.start && range?.end) {
    const startDate = toDate(range.start)
    const endDate = toDate(range.end)

    // Utiliser toLocaleDateString avec format ISO pour éviter les décalages UTC
    chartConfig.period.start = startDate.toLocaleDateString('en-CA') // format YYYY-MM-DD
    chartConfig.period.end = endDate.toLocaleDateString('en-CA')
  }
}

const updateComparisonPeriod = (range: DateRange | undefined) => {
  if (range?.start && range?.end) {
    const startDate = toDate(range.start)
    const endDate = toDate(range.end)

    if (!chartConfig.comparisonPeriod) {
      chartConfig.comparisonPeriod = { start: '', end: '' }
    }
    chartConfig.comparisonPeriod.start = startDate.toLocaleDateString('en-CA')
    chartConfig.comparisonPeriod.end = endDate.toLocaleDateString('en-CA')
  }
}

// Sauvegarde
const handleSave = () => {
  if (!isFormValid.value) return

  const chart: CustomChart = {
    id: props.chart?.id || generateId(),
    name: chartConfig.name.trim(),
    config: {
      chartType: chartConfig.chartType,
      dataSources: chartConfig.dataSources,
      xAxis: chartConfig.xAxis,
      yAxis: chartConfig.yAxis,
      yAxis2: chartConfig.yAxis2,
      filters: chartConfig.filters,
      display: chartConfig.display,
      mobileConfig: chartConfig.mobileConfig,
      period: chartConfig.period,
      showComparison: chartConfig.showComparison,
      comparisonPeriod: chartConfig.comparisonPeriod
    },
    state: {
      loading: false,
      syncStatus: 'synced',
      lastUpdated: new Date()
    },
    centerId: props.centerId,
    reportId: props.reportId,
    createdAt: props.chart?.createdAt || new Date(),
    updatedAt: new Date()
  }

  emit('save', chart)
}

const generateId = (): string => {
  return 'chart_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
}

// Gestion des événements du ChartRenderer
const handleDataPointClick = (data: any) => {
  console.log('Data point clicked:', data)
}

const handleChartReady = (chart: any) => {
  console.log('Chart ready:', chart)
}

const handleChartError = (error: string) => {
  console.error('Chart error:', error)
}

// Utilitaires pour les labels

const getTimeAggregationLabel = (type: string): string => {
  const config = TIME_AGGREGATIONS.find(agg => agg.type === type)
  return config?.label || type
}

const getAggregationLabel = (type: string): string => {
  const config = AGGREGATION_TYPES.find(agg => agg.type === type)
  return config?.label || type
}

const formatConfigPeriod = (): string => {
  if (!chartConfig.period.start || !chartConfig.period.end) return ''
  const start = new Date(chartConfig.period.start).toLocaleDateString('fr-FR')
  const end = new Date(chartConfig.period.end).toLocaleDateString('fr-FR')
  return `${start} - ${end}`
}

// Validation en temps réel
watch(() => chartConfig, (config) => {
  chartValidator.validate(config as FlexibleChartConfig, (result) => {
    validationResult.value = result
  })
}, { deep: true })

// Initialisation
onMounted(() => {
  if (props.chart) {
    // Charger la configuration existante
    Object.assign(chartConfig, {
      name: props.chart.name,
      ...props.chart.config
    })

    // Initialiser les ranges pour les dates
    if (props.chart.config.period.start && props.chart.config.period.end) {
      const startDate = new Date(props.chart.config.period.start)
      const endDate = new Date(props.chart.config.period.end)

      selectedRange.value = {
        start: new CalendarDate(startDate.getFullYear(), startDate.getMonth() + 1, startDate.getDate()),
        end: new CalendarDate(endDate.getFullYear(), endDate.getMonth() + 1, endDate.getDate())
      }
    }
  }
})
</script>

<style scoped>
@reference "../../style.css";

/* Animations de transition entre étapes */
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-left-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.slide-left-leave-to {
  opacity: 0;
  transform: translateX(-100%);
}

.slide-right-enter-from {
  opacity: 0;
  transform: translateX(-100%);
}

.slide-right-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

/* Animation slide-down pour la comparaison */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-down-enter-from {
  opacity: 0;
  transform: scaleY(0.8) translateY(-8px);
}

.slide-down-leave-to {
  opacity: 0;
  transform: scaleY(0.8) translateY(-8px);
}

/* Optimisations tactiles */
.touch-manipulation {
  touch-action: manipulation;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}

/* Z-index pour sticky elements */
.z-10 {
  z-index: 10;
}

.z-9 {
  z-index: 9;
}

/* Amélioration du scroll sur mobile */
.overflow-y-auto {
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
}

/* État disabled pour les boutons */
button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

button:disabled:active {
  transform: none;
}

/* Points de navigation */
.w-2.h-2 {
  transition: all 0.2s ease-in-out;
}

.w-6 {
  width: 1.5rem;
}

/* Style pour les inputs sur mobile */
@media (max-width: 640px) {

  input[type="text"],
  select,
  button {
    font-size: 16px;
    /* Évite le zoom sur iOS */
  }

  .py-3 {
    padding-top: 0.875rem;
    padding-bottom: 0.875rem;
  }
}
</style>